import moment from 'moment';
import { Pagination } from 'antd';
import { Text, mergeClassNames } from '@clockout-dev/design-elements';
import styles from '../styles/feedback.module.scss';

function initials(name) {
    if (!name) return '—';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '—';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] || '' : '';
    return (first + last).toUpperCase();
}

/**
 * Badge background/text colors keyed to the rating band (high → low). Values are
 * admin pill tokens from constants/variables.scss: $reports-pill-success-*,
 * $reports-pill-warn-*, $reports-pill-danger-bg / $text-error, $surface-pill /
 * $text-description.
 */
function badgeColors(rating) {
    if (rating == null) return { background: '#eef2f7', color: '#64748b' };
    if (rating >= 9) return { background: '#dcfce7', color: '#15803d' };
    if (rating >= 7) return { background: '#dcfce7', color: '#16a34a' };
    if (rating >= 5) return { background: '#fef3c7', color: '#b45309' };
    if (rating >= 3) return { background: '#fef3c7', color: '#d97706' };
    return { background: '#fee2e2', color: '#b91c1c' };
}

const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => i);

function RecentComments({ items = [], loading = false, pageInfo = {}, onPageChange }) {
    const total = pageInfo.total || 0;
    const perPage = pageInfo.perPage || items.length || 25;
    const current = pageInfo.current || 1;
    const showPager = total > perPage;

    return (
        <div className={mergeClassNames([styles.card, styles.commentsCard])}>
            <div className={styles.commentsHeader}>Recent comments</div>

            {loading ? (
                <div className={styles.commentsList}>
                    {SKELETON_ROWS.map((i) => (
                        <div key={i} className={styles.commentRow}>
                            <div className={styles.commentTop}>
                                <div className={styles.skeletonLine} style={{ width: 38, height: 38, borderRadius: '50%' }} />
                                <div className={styles.skeletonLine} style={{ width: 120, height: 14 }} />
                            </div>
                            <div className={styles.skeletonLine} style={{ width: '90%', height: 14 }} />
                            <div className={styles.skeletonLine} style={{ width: '70%', height: 14 }} />
                        </div>
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className={styles.empty}>No feedback in this period.</div>
            ) : (
                <div className={styles.commentsList}>
                    {items.map((item) => {
                        const colors = badgeColors(item.rating);
                        return (
                            <div key={item.event_id} className={styles.commentRow}>
                                <div className={styles.commentTop}>
                                    <span className={styles.avatar}>{initials(item.name)}</span>
                                    <Text as="span" className={styles.commentName}>
                                        {item.name || 'Unknown'}
                                    </Text>
                                    {item.rating != null && (
                                        <span
                                            className={styles.scoreBadge}
                                            style={{ background: colors.background, color: colors.color }}
                                        >
                                            {item.rating}/10
                                        </span>
                                    )}
                                    <span className={styles.commentDate}>
                                        {item.timestamp_ms ? moment(item.timestamp_ms).format('MMM DD') : ''}
                                    </span>
                                </div>
                                {item.comments && (
                                    <Text as="p" className={styles.commentBody}>
                                        {item.comments}
                                    </Text>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {!loading && showPager && (
                <div className={styles.commentsFooter}>
                    <Pagination
                        size="small"
                        current={current}
                        pageSize={perPage}
                        total={total}
                        showSizeChanger={false}
                        onChange={(page, size) => onPageChange?.(page, size)}
                    />
                </div>
            )}
        </div>
    );
}

export default RecentComments;
